import { useMemo, useState } from 'react';
import type { PickerProps } from './types';

const DIRECTIVES = [
  'default-src',
  'script-src',
  'style-src',
  'img-src',
  'font-src',
  'connect-src',
  'media-src',
  'object-src',
  'frame-src',
  'frame-ancestors',
  'form-action',
  'base-uri',
  'report-uri',
];

const SOURCE_PRESETS = [
  "'self'",
  "'none'",
  "'unsafe-inline'",
  "'unsafe-eval'",
  '*',
  'data:',
  'blob:',
  'https:',
  'http:',
];

function parse(value: string): Map<string, string> {
  const out = new Map<string, string>();
  for (const part of value.split(';')) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const space = trimmed.indexOf(' ');
    if (space === -1) {
      out.set(trimmed, '');
    } else {
      out.set(trimmed.slice(0, space), trimmed.slice(space + 1));
    }
  }
  return out;
}

function build(map: Map<string, string>): string {
  const parts: string[] = [];
  for (const [k, v] of map) {
    parts.push(v ? `${k} ${v}` : k);
  }
  return parts.join('; ');
}

export function CSPPicker({ value, onChange }: PickerProps) {
  const [directives, setDirectives] = useState<Map<string, string>>(() => parse(value));
  const entries = useMemo(() => Array.from(directives.entries()), [directives]);

  const commit = (next: Map<string, string>) => {
    setDirectives(next);
    onChange(build(next));
  };

  const update = (key: string, val: string) => {
    const next = new Map(directives);
    next.set(key, val);
    commit(next);
  };

  const remove = (key: string) => {
    const next = new Map(directives);
    next.delete(key);
    commit(next);
  };

  const addDirective = (key: string) => {
    if (directives.has(key)) return;
    const next = new Map(directives);
    next.set(key, "'self'");
    commit(next);
  };

  const appendSource = (key: string, source: string) => {
    const current = directives.get(key) ?? '';
    const tokens = current.split(/\s+/).filter(Boolean);
    if (tokens.includes(source)) return;
    update(key, [...tokens, source].join(' '));
  };

  const unused = DIRECTIVES.filter((d) => !directives.has(d));

  return (
    <div className="picker-grid">
      {entries.map(([key, val]) => (
        <div key={key} className="csp-directive">
          <span className="csp-key">{key}</span>
          <input
            className="picker-input"
            value={val}
            onChange={(e) => update(key, e.target.value)}
            placeholder="sources"
          />
          <div className="picker-row">
            {SOURCE_PRESETS.map((src) => (
              <button
                key={src}
                type="button"
                className="pill small"
                onClick={() => appendSource(key, src)}
              >
                + {src}
              </button>
            ))}
            <button
              type="button"
              className="pill small danger"
              onClick={() => remove(key)}
            >
              remove
            </button>
          </div>
        </div>
      ))}
      {unused.length > 0 && (
        <div className="picker-row">
          <span className="picker-label">Add directive:</span>
          {unused.map((d) => (
            <button
              key={d}
              type="button"
              className="pill small"
              onClick={() => addDirective(d)}
            >
              + {d}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
